import * as React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { Person, ProfileMembership } from '@/lib/contacts/types'
import type { MembershipLevel, MembershipLevelsResponse } from '@/pages/MembershipLevelsPage.types'

interface MembershipResponse {
  membership: ProfileMembership
}

interface SeatCurrentDateResponse {
  seat: {
    association: {
      current_date?: string
    }
  }
}

interface AssignMembershipFieldsProps {
  levels: MembershipLevel[]
  levelId: string
  memberSinceOn: string
  activation: ActivationMode
  levelsLoading: boolean
  levelsError: boolean
  dateError: boolean
  fieldErrors: ReturnType<typeof assignMembershipFieldErrors>
  onLevelChange: (value: string) => void
  onMemberSinceChange: (value: string) => void
  onActivationChange: (value: ActivationMode) => void
}

type ActivationMode = 'offline' | 'free' | 'pending_new'

const EMPTY_MEMBERSHIP_LEVELS: MembershipLevel[] = []

export function AssignMembershipDialog({
  person,
  defaultMemberSinceOn,
  onClose,
  onAssigned,
}: {
  person: Person
  defaultMemberSinceOn?: string
  onClose: () => void
  onAssigned: () => Promise<void>
}) {
  const levelsQuery = useQuery({
    queryKey: ['membership-levels', 'available-for-assignment'],
    queryFn: () =>
      getJSON<MembershipLevelsResponse>('/api/v1/membership_levels?available_for_assignment=true'),
    retry: false,
  })
  const currentDateQuery = useQuery({
    queryKey: ['seat-current-date'],
    queryFn: () => getJSON<SeatCurrentDateResponse>('/api/v1/seat'),
    retry: false,
  })
  const levels = levelsQuery.data?.membership_levels ?? EMPTY_MEMBERSHIP_LEVELS
  const [levelId, setLevelId] = React.useState('')
  const [memberSinceOn, setMemberSinceOn] = React.useState(
    () => defaultMemberSinceOn ?? todayInputValue(),
  )
  const [memberSinceEdited, setMemberSinceEdited] = React.useState(false)
  const [activation, setActivation] = React.useState<ActivationMode>('offline')
  const selectedLevel = levels.find((level) => String(level.id) === levelId)
  const assignMembership = useMutation({
    mutationFn: () =>
      postJSON<MembershipResponse>(`/api/v1/persons/${person.id}/membership`, {
        membership: assignMembershipPayload({ levelId, memberSinceOn, activation }),
      }),
    onSuccess: onAssigned,
  })
  const fieldErrors = assignMembershipFieldErrors(assignMembership.error)
  const submitDisabled =
    assignMembership.isPending ||
    levels.length === 0 ||
    currentDateQuery.isLoading ||
    currentDateQuery.isError

  React.useEffect(() => {
    if (!levelId && levels[0]) setLevelId(String(levels[0].id))
  }, [levelId, levels])

  React.useEffect(() => {
    const freshDefault = currentDateQuery.data?.seat.association.current_date
    if (freshDefault && !memberSinceEdited) setMemberSinceOn(freshDefault)
  }, [currentDateQuery.data, memberSinceEdited])

  React.useEffect(() => {
    setActivation((current) => {
      if (current === 'pending_new') return current

      return selectedLevel?.free ? 'free' : 'offline'
    })
  }, [selectedLevel?.free])

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            assignMembership.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>Assign membership</DialogTitle>
            <DialogDescription>{person.name} · Membership assignment</DialogDescription>
          </DialogHeader>

          <AssignMembershipFields
            levels={levels}
            levelId={levelId}
            memberSinceOn={memberSinceOn}
            activation={activation}
            levelsLoading={levelsQuery.isLoading}
            levelsError={levelsQuery.isError}
            dateError={currentDateQuery.isError}
            fieldErrors={fieldErrors}
            onLevelChange={setLevelId}
            onMemberSinceChange={(value) => {
              setMemberSinceEdited(true)
              setMemberSinceOn(value)
            }}
            onActivationChange={setActivation}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitDisabled}>
              {assignMembershipSubmitLabel(assignMembership.isPending, activation)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function AssignMembershipFields({
  levels,
  levelId,
  memberSinceOn,
  activation,
  levelsLoading,
  levelsError,
  dateError,
  fieldErrors,
  onLevelChange,
  onMemberSinceChange,
  onActivationChange,
}: AssignMembershipFieldsProps) {
  return (
    <div className="flex flex-col gap-4">
      {levelsError && <Alert>Membership Levels unavailable.</Alert>}
      {dateError && <Alert>Association date unavailable.</Alert>}
      {fieldErrors.person && <Alert>{fieldErrors.person}</Alert>}
      <MembershipLevelSelect
        levels={levels}
        value={levelId}
        label="Level"
        htmlFor="assign-membership-level"
        error={fieldErrors.membershipLevel}
        loading={levelsLoading}
        onChange={onLevelChange}
      />
      {activation !== 'pending_new' && (
        <FormField
          label="Member since"
          htmlFor="assign-member-since"
          error={fieldErrors.memberSince}
        >
          <Input
            id="assign-member-since"
            type="date"
            value={memberSinceOn}
            onChange={(event) => onMemberSinceChange(event.target.value)}
          />
        </FormField>
      )}
      <ActivationSelect
        value={activation}
        error={fieldErrors.paymentHandling}
        onChange={onActivationChange}
      />
    </div>
  )
}

function MembershipLevelSelect({
  levels,
  value,
  label,
  htmlFor,
  loading,
  error,
  onChange,
}: {
  levels: MembershipLevel[]
  value: string
  label: string
  htmlFor: string
  loading: boolean
  error?: string
  onChange: (value: string) => void
}) {
  return (
    <FormField label={label} htmlFor={htmlFor} error={error}>
      <Select value={value} disabled={loading || levels.length === 0} onValueChange={onChange}>
        <SelectTrigger id={htmlFor}>
          <SelectValue placeholder={loading ? 'Loading levels' : 'Select a level'} />
        </SelectTrigger>
        <SelectContent>
          {levels.map((level) => (
            <SelectItem key={level.id} value={String(level.id)}>
              {level.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  )
}

function ActivationSelect({
  value,
  error,
  onChange,
}: {
  value: ActivationMode
  error?: string
  onChange: (value: ActivationMode) => void
}) {
  return (
    <FormField label="Activation" htmlFor="assign-membership-activation" error={error}>
      <Select value={value} onValueChange={(nextValue) => onChange(nextValue as ActivationMode)}>
        <SelectTrigger id="assign-membership-activation">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="offline">Activate — payment handled offline</SelectItem>
          <SelectItem value="free">Activate — free level (no payment)</SelectItem>
          <SelectItem value="pending_new">Pending-New application</SelectItem>
        </SelectContent>
      </Select>
    </FormField>
  )
}

function assignMembershipPayload({
  levelId,
  memberSinceOn,
  activation,
}: {
  levelId: string
  memberSinceOn: string
  activation: ActivationMode
}) {
  const membershipLevelId = levelId ? Number(levelId) : null
  if (activation === 'pending_new') {
    return {
      membership_level_id: membershipLevelId,
      application: true,
    }
  }

  return {
    membership_level_id: membershipLevelId,
    member_since_on: memberSinceOn,
    payment_handling: activation === 'offline' ? 'offline' : null,
  }
}

function assignMembershipSubmitLabel(isPending: boolean, activation: ActivationMode) {
  if (activation === 'pending_new') return isPending ? 'Creating...' : 'Create application'

  return isPending ? 'Activating...' : 'Activate membership'
}

function assignMembershipFieldErrors(error: Error | null) {
  return {
    membershipLevel: apiFieldMessages(error, 'membership_level')[0],
    memberSince: apiFieldMessages(error, 'member_since_on')[0],
    paymentHandling: apiFieldMessages(error, 'payment_handling')[0],
    person: apiFieldMessages(error, 'person')[0],
  }
}

function todayInputValue() {
  return new Date().toISOString().slice(0, 10)
}
